import type { CourseCardCartItem, CourseCardItem, CourseCardStagePricingItem } from '../types/course-card';

export interface ResolvedCourseCardStagePricing {
  stages: CourseCardStagePricingItem[];
  activeStage: CourseCardStagePricingItem;
  activeStageIndex: number;
  nextStage?: CourseCardStagePricingItem;
  isLastStage: boolean;
}

export const computeCourseCardActiveStageIndexByPrice = (
  stages: CourseCardStagePricingItem[],
  price: number,
): number => {
  if (stages.length === 0) return 0;

  const exactIndex = stages.findIndex((stage) => stage.price === price);
  if (exactIndex >= 0) return exactIndex;

  const lastAffordableIndex = stages.findLastIndex((stage) => stage.price <= price);
  if (lastAffordableIndex >= 0) return lastAffordableIndex;

  return 0;
};

export const resolveCourseCardStagePricing = (
  price: number,
  stagePricing?: CourseCardStagePricingItem[],
): ResolvedCourseCardStagePricing | null => {
  if (!stagePricing?.length) return null;

  const stages = [...stagePricing].sort((firstStage, secondStage) => firstStage.stage - secondStage.stage);
  const activeStageIndex = computeCourseCardActiveStageIndexByPrice(stages, price);
  const activeStage = stages[activeStageIndex];

  if (!activeStage) return null;

  return {
    stages,
    activeStage,
    activeStageIndex,
    nextStage: stages[activeStageIndex + 1],
    isLastStage: activeStageIndex === stages.length - 1,
  };
};

export const mapCourseCardToCartItem = (course: CourseCardItem): CourseCardCartItem => {
  const stagePricing = resolveCourseCardStagePricing(course.price, course.stagePricing);

  return {
    id: course.id,
    slug: course.slug,
    title: course.title,
    image_url: course.imageUrl,
    price: course.price,
    original_price: course.originalPrice,
    currency: course.currency,
    instructor_name: course.instructorName,
    price_expires_at: stagePricing?.activeStage.deadline ?? undefined,
    next_price: stagePricing?.nextStage?.price,
  };
};
